/**
 * «Importar desde el Drive» (administración, 20/10/2026).
 *
 * Los consentimientos y los postoperatorios de cada médico están en el Drive
 * de la app, en Word y PDF. Los textos ENTRAN COMO DATOS: desde aquí un
 * administrador elige una carpeta (o un documento) y el servidor crea
 * BORRADORES, inactivos y marcados «importado de: <ruta>», que el médico
 * revisa y un administrador activa (backend/services/importar_drive.py).
 * Nunca se activan solos: son documentos legales.
 *
 * Los postoperatorios son de un médico: se pregunta de cuál antes de
 * importar. El resultado dice qué se creó (con su estructura: secciones,
 * palabras, marcadores, si habla de anestesia y los avisos), qué ya estaba y
 * qué no se pudo leer.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { FileText, Folder, Loader2, Upload } from 'lucide-react';
import { api, mensajeError } from '../lib/api';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';

const RUTAS = {
  consentimiento: '/api/consent-templates/importar-drive',
  postoperatorio: '/api/postoperatorios/templates/importar-drive',
};

/** Las carpetas en árbol, cada una con su profundidad, en orden. */
export function carpetasEnArbol(carpetas) {
  const lista = Array.isArray(carpetas) ? carpetas : [];
  const hijas = {};
  lista.forEach((c) => {
    const padre = c.parent_id || '';
    (hijas[padre] = hijas[padre] || []).push(c);
  });
  Object.values(hijas).forEach((h) => h.sort((a, b) => String(a.name).localeCompare(String(b.name))));
  const salida = [];
  const vistas = new Set();
  const recorrer = (padre, nivel) => {
    (hijas[padre] || []).forEach((c) => {
      if (vistas.has(c.id)) return;
      vistas.add(c.id);
      salida.push({ ...c, nivel });
      recorrer(c.id, nivel + 1);
    });
  };
  recorrer('', 0);
  return salida;
}

function Resultado({ resultado }) {
  const { importados = [], repetidos = [], fallidos = [] } = resultado || {};
  return (
    <div className="space-y-3 text-sm" data-testid="importar-drive-resultado">
      <p className="font-medium text-[#13231E]">
        {importados.length
          ? `${importados.length} borrador${importados.length === 1 ? '' : 'es'} creado${importados.length === 1 ? '' : 's'}, sin activar: revísalos y actívalos desde el editor.`
          : 'No se ha creado ningún borrador.'}
      </p>
      {importados.map((h) => (
        <div key={h.id} className="border border-border rounded-md p-2" data-testid="importar-drive-creado">
          <div className="font-medium">{h.nombre}</div>
          <div className="text-xs text-muted-foreground">Importado de: {h.ruta}</div>
          <div className="text-xs mt-1">
            {h.secciones} secciones{h.secciones_a_revisar ? ` (${h.secciones_a_revisar} «a revisar»)` : ''}
            {' · '}{h.palabras} palabras
            {' · '}marcadores: médico {h.marcadores?.DOCTOR || 0}, paciente {h.marcadores?.PACIENTE || 0}
            {h.huecos_sin_marcador ? ` · ${h.huecos_sin_marcador} huecos sin marcador` : ''}
            {h.anestesia ? ' · habla de anestesia (marca propuesta, sin poner)' : ''}
          </div>
          {(h.avisos || []).length > 0 && (
            <ul className="list-disc pl-5 mt-1 text-xs text-amber-800">
              {h.avisos.map((a) => <li key={a}>{a}</li>)}
            </ul>
          )}
        </div>
      ))}
      {repetidos.length > 0 && (
        <div data-testid="importar-drive-repetidos">
          <div className="text-xs font-semibold">Ya estaban (no se crea otro)</div>
          <ul className="list-disc pl-5 text-xs">{repetidos.map((x) => <li key={x.ruta}>{x.ruta}: {x.motivo}</li>)}</ul>
        </div>
      )}
      {fallidos.length > 0 && (
        <div data-testid="importar-drive-fallidos">
          <div className="text-xs font-semibold">No se han podido importar</div>
          <ul className="list-disc pl-5 text-xs">{fallidos.map((x) => <li key={x.ruta}>{x.ruta}: {x.motivo}</li>)}</ul>
        </div>
      )}
    </div>
  );
}

export default function ImportarDelDriveDialog({ open, onOpenChange, tipo, surgeons = [], onImportado }) {
  const [carpetas, setCarpetas] = useState(null);   // null = cargando
  const [error, setError] = useState('');
  const [carpeta, setCarpeta] = useState('');
  const [docs, setDocs] = useState([]);
  const [docId, setDocId] = useState('');
  const [subcarpetas, setSubcarpetas] = useState(false);
  const [surgeonId, setSurgeonId] = useState('');
  const [importando, setImportando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const esPostop = tipo === 'postoperatorio';
  // El titular también puede tener sus postoperatorios; se ofrecen todos.
  const medicos = useMemo(() => (Array.isArray(surgeons) ? surgeons : []), [surgeons]);

  useEffect(() => {
    if (!open) return undefined;
    let vivo = true;
    setCarpetas(null); setError(''); setCarpeta(''); setDocs([]); setDocId('');
    setSubcarpetas(false); setResultado(null); setSurgeonId('');
    api.get('/api/drive/carpetas')
      .then((r) => { if (vivo) setCarpetas(Array.isArray(r?.data) ? r.data : []); })
      .catch((e) => { if (vivo) { setCarpetas([]); setError(mensajeError(e, 'No se ha podido leer el Drive')); } });
    return () => { vivo = false; };
  }, [open]);

  useEffect(() => {
    if (!carpeta) { setDocs([]); return undefined; }
    let vivo = true;
    setDocId('');
    api.get('/api/drive', { params: { folder_id: carpeta } })
      .then((r) => { if (vivo) setDocs(Array.isArray(r?.data?.docs) ? r.data.docs : []); })
      .catch(() => { if (vivo) setDocs([]); });
    return () => { vivo = false; };
  }, [carpeta]);

  const arbol = useMemo(() => carpetasEnArbol(carpetas), [carpetas]);
  const puede = !!carpeta && (!esPostop || !!surgeonId) && !importando;

  const importar = async () => {
    setImportando(true);
    try {
      const cuerpo = docId
        ? { doc_id: docId }
        : { folder_id: carpeta, incluir_subcarpetas: subcarpetas };
      if (esPostop) cuerpo.surgeon_id = surgeonId;
      const r = await api.post(RUTAS[tipo], cuerpo);
      setResultado(r?.data || {});
      const n = (r?.data?.importados || []).length;
      toast.success(n ? `${n} borrador${n === 1 ? '' : 'es'} importado${n === 1 ? '' : 's'}` : 'No se ha importado nada nuevo');
      onImportado?.(r?.data);
    } catch (e) {
      toast.error(mensajeError(e, 'No se ha podido importar'));
    } finally {
      setImportando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="importar-drive-dialog">
        <DialogHeader>
          <DialogTitle>Importar {esPostop ? 'postoperatorios' : 'consentimientos'} desde el Drive</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground -mt-2">
          Se crean BORRADORES sin activar, marcados «importado de: …», con el título como nombre, los
          apartados como secciones{esPostop ? '' : ' y el «Consiento / autorizo» final como texto del consentimiento'}.
          Los huecos «Dr./Dra. ____» y «D./Dña. ____» pasan a ser los marcadores del médico y del paciente; lo
          que no se sabe ordenar va a «Texto importado (revisar)». Son documentos legales: el médico los revisa
          y un administrador los activa. Nunca se activan solos. Word (.docx) y PDF con texto.
        </p>
        {resultado ? <Resultado resultado={resultado} /> : (
          <div className="space-y-3">
            {esPostop && (
              <div>
                <Label htmlFor="importar-drive-medico">De qué médico son</Label>
                <select id="importar-drive-medico" value={surgeonId} onChange={(e) => setSurgeonId(e.target.value)}
                  className="mt-1 w-full border border-border rounded-md h-9 px-2 text-sm bg-white"
                  data-testid="importar-drive-medico">
                  <option value="">Elige el médico…</option>
                  {medicos.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            )}
            <div>
              <Label>Carpeta del Drive</Label>
              {carpetas === null ? (
                <p className="text-sm text-muted-foreground flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Cargando…</p>
              ) : error ? (
                <p className="text-sm text-red-700" role="alert">{error}</p>
              ) : arbol.length === 0 ? (
                <p className="text-sm text-muted-foreground">El Drive no tiene carpetas.</p>
              ) : (
                <div className="border border-border rounded-md max-h-56 overflow-y-auto mt-1" data-testid="importar-drive-carpetas">
                  {arbol.map((c) => (
                    <button key={c.id} type="button" onClick={() => setCarpeta(c.id)}
                      className={`w-full text-left text-sm px-2 py-1 flex items-center gap-1.5 ${carpeta === c.id ? 'bg-[#2E6E5E]/15 font-medium' : 'hover:bg-muted/50'}`}
                      style={{ paddingLeft: `${8 + c.nivel * 16}px` }}
                      data-testid={`importar-drive-carpeta-${c.id}`}>
                      <Folder className="w-3.5 h-3.5 text-[#2E6E5E]" /> {c.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {carpeta && (
              <div className="space-y-2">
                <label className="text-sm flex items-center gap-2">
                  <input type="radio" name="importar-que" checked={!docId} onChange={() => setDocId('')}
                    data-testid="importar-drive-toda" />
                  Toda la carpeta ({docs.length} documento{docs.length === 1 ? '' : 's'})
                </label>
                {!docId && (
                  <label className="text-xs flex items-center gap-2 pl-6">
                    <input type="checkbox" checked={subcarpetas} onChange={(e) => setSubcarpetas(e.target.checked)}
                      data-testid="importar-drive-subcarpetas" />
                    y sus subcarpetas
                  </label>
                )}
                {docs.map((d) => (
                  <label key={d.id} className="text-sm flex items-center gap-2">
                    <input type="radio" name="importar-que" checked={docId === d.id} onChange={() => setDocId(d.id)}
                      data-testid={`importar-drive-doc-${d.id}`} />
                    <FileText className="w-3.5 h-3.5 text-muted-foreground" /> Solo «{d.name}»
                  </label>
                ))}
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {resultado ? 'Cerrar' : 'Cancelar'}
          </Button>
          {!resultado && (
            <Button type="button" onClick={importar} disabled={!puede}
              className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="importar-drive-importar">
              {importando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Importar como borradores
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
